import useApplyProposalTemplate from '@/api/mutations/proposals/useApplyProposalTemplate';
import projectKeys from '@/api/queries/project/keyFactory';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import { useGetProposal } from '@/api/queries/proposal/useGetProposal';
import TemplateSelectionControl from '@/ui/components/templateSelectionControl/TemplateSelectionControl';
import { Button, Modal, useToastContext, Text } from '@payaca/ambrosia';
import { PublicHydratedEntityTemplate } from '@payaca/types/entity-templates';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

type ApplyProposalTemplateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Public id of the Proposal being edited (the route param). */
  proposalId: string;
  /** Public id of the parent Project, used for cache invalidation. */
  projectId: string;
  /**
   * Called once the applied Template's content has been refetched, so the
   * builder can remount its content editors onto the fresh content.
   */
  onTemplateApplied: () => void;
};

const ApplyProposalTemplateModal: FC<ApplyProposalTemplateModalProps> = (
  props
) => {
  const { isOpen, onClose, proposalId, projectId, onTemplateApplied } = props;

  const { proposal } = useGetProposal(proposalId);
  const queryClient = useQueryClient();
  const { pushSuccess } = useToastContext();

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<PublicHydratedEntityTemplate<'proposal'>>();

  const { mutate: applyTemplate, isPending: isApplyingTemplate } =
    useApplyProposalTemplate({
      onSuccess: async () => {
        // Await the refetch so the fresh content is in the cache before the
        // builder remounts its editors onto it.
        await queryClient.refetchQueries({
          queryKey: proposalKeys.proposal(proposalId),
        });
        void queryClient.invalidateQueries({
          queryKey: projectKeys.project(projectId),
        });
        onTemplateApplied();
        pushSuccess('Template applied');
        onClose();
      },
    });

  useEffect(() => {
    if (isOpen) {
      setShowConfirmation(false);
      setSelectedTemplate(undefined);
    }
  }, [isOpen]);

  // The sections of the Proposal that applying a Template will overwrite.
  // Derived from the current Proposal so the confirmation lists only what the
  // user actually stands to lose. Always includes the Proposal type, so the
  // confirmation step is always shown.
  const sectionsWhichWillBeOverwritten = useMemo(() => {
    const sections = ['Proposal type (quote or estimate)'];

    proposal?.sections.forEach((section) => {
      if (section.__typename === 'ProposalContentSection' && section.content) {
        sections.push(section.title ?? 'Content');
      }
    });

    if (proposal?.paymentSchedule?.stages.length) {
      sections.push('Payment schedule');
    }
    if (proposal?.validForDays != null) {
      sections.push('Valid for (days)');
    }

    return sections;
  }, [proposal]);

  const handleApply = useCallback(() => {
    if (!selectedTemplate?.publicId) return;

    applyTemplate({
      proposalId,
      templateId: selectedTemplate.publicId,
    });
  }, [selectedTemplate?.publicId, proposalId, applyTemplate]);

  return (
    <Modal
      size="md"
      isOpen={isOpen}
      onClose={onClose}
      title={
        showConfirmation
          ? `Apply “${selectedTemplate?.name}” template?`
          : 'Select a template'
      }
    >
      {!showConfirmation ? (
        <>
          <Modal.Body>
            <TemplateSelectionControl<'proposal'>
              selectedTemplate={selectedTemplate}
              label="Choose a template"
              onChange={setSelectedTemplate}
              allowedEntityTypes={['proposal']}
            />
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                disabled={!selectedTemplate || isApplyingTemplate}
                isProcessing={isApplyingTemplate}
                onClick={() => setShowConfirmation(true)}
              >
                Next
              </Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </>
      ) : (
        <>
          <Modal.Body>
            <div className="space-y-4">
              <Text>
                Applying this template will overwrite the following sections of
                the proposal:
              </Text>
              <ul>
                {sectionsWhichWillBeOverwritten.map((section) => (
                  <li key={section}>{section}</li>
                ))}
              </ul>
              <Text>
                Any existing content in these sections will be replaced.
              </Text>
              <Text>
                <strong className="font-bold">
                  Would you like to continue?
                </strong>
              </Text>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                colour="negative"
                isProcessing={isApplyingTemplate}
                disabled={isApplyingTemplate}
                onClick={handleApply}
              >
                Overwrite
              </Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default ApplyProposalTemplateModal;
